import countries from '../data/countries.json';

const sortedByCodeLength = [...countries].sort(
  (a, b) => b.callingCode.length - a.callingCode.length
);

export function isoToFlagEmoji(iso2) {
  return [...iso2.toUpperCase()].map(
    (c) => String.fromCodePoint(127397 + c.charCodeAt(0))
  ).join('');
}

export function findCountryByIso2(iso2) {
  return countries.find((c) => c.iso2 === iso2) || null;
}

export function findCountryByCallingCode(digits) {
  for (const country of sortedByCodeLength) {
    if (digits.startsWith(country.callingCode)) {
      return {
        country,
        nationalNumber: digits.slice(country.callingCode.length),
      };
    }
  }
  return null;
}

export function detectCountryFromInput(rawDigits) {
  if (!rawDigits) return null;
  let digits = rawDigits.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  return findCountryByCallingCode(digits);
}

/** True when the user entered an explicit international prefix (+ or 00). */
export function looksLikeInternationalInput(trimmed, digits) {
  if (!trimmed) return false;
  if (trimmed.startsWith('+')) return true;
  const d = digits || trimmed.replace(/\D/g, '');
  return d.startsWith('00');
}

function parseAsNationalNumber(digits, country) {
  let nationalNumber = digits;
  if (nationalNumber.startsWith('0')) {
    nationalNumber = nationalNumber.replace(/^0+/, '');
  }
  if (nationalNumber.startsWith(country.callingCode)) {
    nationalNumber = nationalNumber.slice(country.callingCode.length);
  }
  return nationalNumber;
}

/**
 * Parse user input into canonical E.164 digits (country code + national number, no +).
 */
export function parsePhoneInput(rawInput, selectedCountry) {
  const trimmed = (rawInput || '').trim();
  if (!trimmed) {
    return { error: 'Please enter a phone number' };
  }

  let digits = trimmed.replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);

  let country = selectedCountry;
  let nationalNumber = digits;
  const explicitInternational = looksLikeInternationalInput(trimmed, digits);

  if (explicitInternational) {
    const detected = detectCountryFromInput(digits);
    if (detected && detected.nationalNumber.length >= 4) {
      country = detected.country;
      nationalNumber = detected.nationalNumber;
    } else if (country) {
      nationalNumber = parseAsNationalNumber(digits, country);
    } else {
      return { error: 'Select a country or include an international country code' };
    }
  } else if (country) {
    nationalNumber = parseAsNationalNumber(digits, country);
  } else {
    const detected = detectCountryFromInput(digits);
    if (detected && detected.nationalNumber.length >= 4) {
      country = detected.country;
      nationalNumber = detected.nationalNumber;
    } else {
      return { error: 'Select a country or include an international country code' };
    }
  }

  if (!country) {
    return { error: 'Select a country for this phone number' };
  }

  nationalNumber = nationalNumber.replace(/^0+/, '');
  if (!nationalNumber || nationalNumber.length < 4) {
    return { error: 'Phone number is too short' };
  }

  const canonicalId = `${country.callingCode}${nationalNumber}`;

  return buildParsedResult({
    canonicalId,
    country,
    nationalNumber,
    redirect: canonicalId !== digits ? canonicalId : null,
  });
}

/** Consistent shape for Firestore upsert and UI (no undefined fields). */
export function buildParsedResult({ canonicalId, country, nationalNumber, redirect = null }) {
  return {
    canonicalId,
    countryCallingCode: country.callingCode,
    nationalNumber,
    displayE164: `+${canonicalId}`,
    country,
    iso2: country.iso2,
    countryName: country.name,
    redirect,
  };
}

/**
 * Adjacent canonical IDs for internal linking (numeric neighbors + prefix cluster).
 */
export function getAdjacentCanonicalIds(canonicalId, countryCallingCode, nationalNumber) {
  const ids = new Set();
  const nationalStr = String(nationalNumber);

  if (nationalStr.length > 0 && /^\d+$/.test(nationalStr)) {
    const base = parseInt(nationalStr, 10);
    if (!Number.isNaN(base)) {
      for (const delta of [-100, -10, -3, -2, -1, 1, 2, 3, 10, 100]) {
        const next = base + delta;
        if (next > 0) {
          ids.add(`${countryCallingCode}${String(next)}`);
        }
      }
    }
  }

  if (nationalStr.length >= 6) {
    const prefixLen = Math.min(4, nationalStr.length - 2);
    const prefix = nationalStr.slice(0, prefixLen);
    const suffixLen = nationalStr.length - prefixLen;
    const suffixBase = parseInt(nationalStr.slice(prefixLen), 10);
    if (!Number.isNaN(suffixBase)) {
      for (let d = -3; d <= 3; d += 1) {
        const suffix = suffixBase + d;
        if (suffix >= 0) {
          const padded = String(suffix).padStart(suffixLen, '0');
          ids.add(`${countryCallingCode}${prefix}${padded}`);
        }
      }
    }
  }

  ids.delete(canonicalId);
  return [...ids].slice(0, 12);
}

/**
 * Normalize a URL path segment to canonicalId; returns null if invalid.
 */
export function canonicalizeUrlParam(param, fallbackCountry = null) {
  if (!param) return { error: 'Missing phone number' };

  const trimmed = String(param).trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return { error: 'Invalid phone number in URL' };

  const explicitInternational = looksLikeInternationalInput(trimmed, digits);

  if (explicitInternational) {
    const detected = detectCountryFromInput(digits);
    if (detected && detected.nationalNumber.length >= 4) {
      const { country, nationalNumber } = detected;
      const canonicalId = `${country.callingCode}${nationalNumber}`;
      return buildParsedResult({
        canonicalId,
        country,
        nationalNumber,
        redirect: canonicalId !== digits ? canonicalId : null,
      });
    }
  }

  if (fallbackCountry) {
    return parsePhoneInput(param, fallbackCountry);
  }

  const detected = detectCountryFromInput(digits);
  if (detected && detected.nationalNumber.length >= 4) {
    const { country, nationalNumber } = detected;
    const canonicalId = `${country.callingCode}${nationalNumber}`;
    return buildParsedResult({
      canonicalId,
      country,
      nationalNumber,
      redirect: canonicalId !== digits ? canonicalId : null,
    });
  }

  return { error: 'Could not determine country code for this number' };
}

export function formatDisplayNumber(phone) {
  if (phone?.displayE164) return phone.displayE164;
  if (phone?.canonicalId) return `+${phone.canonicalId}`;
  return phone?.number || '';
}

function groupDigits(digits, pattern) {
  const parts = [];
  let idx = 0;
  for (const len of pattern) {
    if (idx >= digits.length) break;
    parts.push(digits.slice(idx, idx + len));
    idx += len;
  }
  if (idx < digits.length) {
    parts.push(digits.slice(idx));
  }
  return parts.join(' ');
}

function formatAuNationalDigits(national) {
  if (!national) return '';
  if (national.startsWith('4') && national.length >= 9) {
    return groupDigits(national, [3, 3, 3]);
  }
  if (national.length >= 9) {
    return groupDigits(national, [1, 4, 4]);
  }
  if (national.length >= 8) {
    return groupDigits(national, [1, 3, 4]);
  }
  return national;
}

function formatGenericNationalDigits(national) {
  if (!national) return '';
  if (national.length <= 6) return national;
  const head = national.slice(0, 3);
  const rest = national.slice(3);
  const tail = rest.match(/.{1,4}/g)?.join(' ') || rest;
  return `${head} ${tail}`.trim();
}

/**
 * International display: +61 7 1234 5678 (national without trunk 0).
 */
export function formatInternationalDisplay(phone) {
  const cc = phone?.countryCallingCode || '';
  const national = String(phone?.nationalNumber || '').replace(/^0+/, '');
  if (!cc && phone?.canonicalId) {
    const detected = findCountryByCallingCode(phone.canonicalId);
    if (detected) {
      return formatInternationalDisplay({
        countryCallingCode: detected.country.callingCode,
        nationalNumber: detected.nationalNumber,
        iso2: detected.country.iso2,
      });
    }
    return formatDisplayNumber(phone);
  }
  if (!national) return formatDisplayNumber(phone);

  const iso2 = (phone?.iso2 || '').toUpperCase();
  const grouped =
    iso2 === 'AU' ? formatAuNationalDigits(national) : formatGenericNationalDigits(national);
  return `+${cc} ${grouped}`.trim();
}

/**
 * Local/national display: 07 1234 5678 (AU adds leading 0).
 */
export function formatNationalDisplay(phone) {
  const national = String(phone?.nationalNumber || '').replace(/^0+/, '');
  if (!national) return '';

  const iso2 = (phone?.iso2 || '').toUpperCase();
  let localDigits = national;
  if (iso2 === 'AU' && /^[23478]/.test(national)) {
    localDigits = `0${national}`;
  }

  if (iso2 === 'AU') {
    const withoutTrunk = localDigits.replace(/^0/, '');
    const grouped = formatAuNationalDigits(withoutTrunk);
    return localDigits.startsWith('0') ? `0${grouped}` : grouped;
  }

  return formatGenericNationalDigits(localDigits);
}

/** Both international and local formatted lines for UI. */
export function getPhoneDisplayLines(phone) {
  const international = formatInternationalDisplay(phone);
  const local = formatNationalDisplay(phone);
  return {
    international,
    local: local && local !== international ? local : '',
    compact: formatDisplayNumber(phone),
  };
}

export { countries };
